import { useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { AdminShell, AdminButton, AdminAlert, AdminInput } from './AdminShared'
import { adminResetPassword } from '../../services/admin'

export default function AdminResetPassword() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const id = params.get('id') ?? ''
  const token = params.get('token') ?? ''

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [showPw, setShowPw] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    if (!id || !token) { setError('Invalid or expired reset link.'); return }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return }
    if (password !== confirm) { setError('Passwords do not match.'); return }
    setLoading(true)
    try {
      await adminResetPassword(id, token, password)
      setDone(true)
      setTimeout(() => navigate('/portal-ax92-v1'), 3000)
    } catch (err) {
      setError(err.message || 'Reset failed. The link may have expired.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <AdminShell>
      <div className="w-full max-w-[480px]">
        <div
          className="bg-surface border-[4px] border-on-background p-8 flex flex-col gap-6"
          style={{ boxShadow: '8px 8px 0px 0px #000' }}
        >
          <div className="text-center flex flex-col items-center gap-3">
            <div
              className="w-16 h-16 bg-on-surface border-[4px] border-on-surface flex items-center justify-center"
              style={{ boxShadow: '4px 4px 0px 0px #d4af37' }}
            >
              <span
                className="material-symbols-outlined text-[32px] text-surface"
                style={{ fontVariationSettings: "'FILL' 1" }}
              >
                key
              </span>
            </div>
            <h1 className="font-extrabold text-[28px] uppercase tracking-tight leading-none text-on-surface">
              New Password
            </h1>
            <p className="text-on-surface-variant font-medium text-[14px]">
              Choose a strong new password for your admin account.
            </p>
          </div>

          {error && <AdminAlert type="error" message={error} />}

          {done ? (
            <div className="flex flex-col gap-4">
              <AdminAlert type="success" message="Password updated! Redirecting to login…" />
            </div>
          ) : (!id || !token) ? (
            <div className="flex flex-col gap-4">
              <AdminAlert type="error" message="Invalid or missing reset link. Please request a new one." />
              <a
                href="/portal-ax92-v1/forgot-password"
                className="block text-center font-bold text-[11px] uppercase tracking-widest text-on-surface-variant hover:text-on-surface transition-colors"
              >
                Request new link
              </a>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-5">
              <div className="flex flex-col gap-2">
                <label className="font-bold text-[11px] uppercase tracking-[0.12em] text-on-surface-variant">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showPw ? 'text' : 'password'}
                    placeholder="Min. 8 characters"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    disabled={loading}
                    style={{ boxShadow: '4px 4px 0px 0px #000' }}
                    className="w-full px-5 py-4 pr-14 border-[4px] border-on-surface bg-surface text-on-surface placeholder:text-on-surface-variant/40 focus:ring-0 focus:outline-none focus:border-primary transition-colors"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(v => !v)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-on-surface-variant hover:text-on-surface transition-colors"
                  >
                    <span className="material-symbols-outlined text-[22px]">
                      {showPw ? 'visibility_off' : 'visibility'}
                    </span>
                  </button>
                </div>
              </div>

              <AdminInput
                id="confirm"
                label="Confirm Password"
                type="password"
                placeholder="Repeat your new password"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                disabled={loading}
              />

              <AdminButton type="submit" loading={loading}>
                <span className="material-symbols-outlined text-[18px]">lock_reset</span>
                Set New Password
              </AdminButton>

              <a
                href="/portal-ax92-v1"
                className="block text-center font-bold text-[11px] uppercase tracking-widest text-on-surface-variant hover:text-on-surface transition-colors"
              >
                ← Back to login
              </a>
            </form>
          )}
        </div>
      </div>
    </AdminShell>
  )
}
